import React, { useState } from "react";
import ChatTrackerNav from "./ChatTrackerNav";

const ChatTrackerSettings = () => {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [autoArchiveDays, setAutoArchiveDays] = useState(30);
  const [negativeSentimentAlert, setNegativeSentimentAlert] = useState(true);
  const [taggingEnabled, setTaggingEnabled] = useState(true);
  const [tags, setTags] = useState([
    "Technical Issue",
    "Billing Question",
    "Feature Request",
    "Bug Report",
    "General Inquiry",
  ]);
  const [newTag, setNewTag] = useState("");
  const [activeSection, setActiveSection] = useState("general");
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() !== "" && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage("");

    // Simulate API call
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSaveMessage("Settings saved successfully!");

      // Clear message after 3 seconds
      setTimeout(() => {
        setSaveMessage("");
      }, 3000);
    } catch (error) {
      setSaveMessage("Error saving settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          General Settings
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure general settings for the conversation tracker.
        </p>
      </div>

      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-gray-900">
                Notifications
              </h3>
              <p className="text-sm text-gray-500">
                Enable or disable notifications for new conversations and
                updates.
              </p>
            </div>
            <button
              type="button"
              className={`${
                notificationsEnabled ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              onClick={() => setNotificationsEnabled(!notificationsEnabled)}
              aria-pressed="false"
              aria-labelledby="toggle-notifications"
            >
              <span className="sr-only">Toggle notifications</span>
              <span
                aria-hidden="true"
                className={`${
                  notificationsEnabled ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              />
            </button>
          </div>
        </div>

        <div className="py-4 space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-gray-900">
                Email Notifications
              </h3>
              <p className="text-sm text-gray-500">
                Receive email notifications for important conversation updates.
              </p>
            </div>
            <button
              type="button"
              className={`${
                emailNotifications ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              onClick={() => setEmailNotifications(!emailNotifications)}
              aria-pressed="false"
              aria-labelledby="toggle-email-notifications"
            >
              <span className="sr-only">Toggle email notifications</span>
              <span
                aria-hidden="true"
                className={`${
                  emailNotifications ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              />
            </button>
          </div>
        </div>

        <div className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-gray-900">
                Auto-archive Old Conversations
              </h3>
              <p className="text-sm text-gray-500">
                Automatically archive conversations after a set number of days.
              </p>
            </div>
            <div>
              <select
                id="auto-archive-days"
                name="auto-archive-days"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                value={autoArchiveDays}
                onChange={(e) => setAutoArchiveDays(Number(e.target.value))}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>365 days</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAlertSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Alerts & Notifications
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure when and how you want to be alerted about conversations.
        </p>
      </div>

      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-gray-900">
                Negative Sentiment Alert
              </h3>
              <p className="text-sm text-gray-500">
                Get alerted when a conversation is detected with negative
                sentiment.
              </p>
            </div>
            <button
              type="button"
              className={`${
                negativeSentimentAlert ? "bg-blue-600" : "bg-gray-200"
              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              onClick={() => setNegativeSentimentAlert(!negativeSentimentAlert)}
              aria-pressed="false"
              aria-labelledby="toggle-negative-sentiment"
            >
              <span className="sr-only">Toggle negative sentiment alerts</span>
              <span
                aria-hidden="true"
                className={`${
                  negativeSentimentAlert ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              />
            </button>
          </div>
        </div>

        <div className="py-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h3 className="text-sm font-medium text-gray-900">
                Alert Recipients
              </h3>
              <p className="text-sm text-gray-500">
                Specify who should receive alerts about important conversations.
              </p>
            </div>
          </div>

          <div className="mt-2">
            <div className="relative flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="admin-alerts"
                  name="admin-alerts"
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  defaultChecked
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="admin-alerts"
                  className="font-medium text-gray-700"
                >
                  Admin Team
                </label>
                <p className="text-gray-500">
                  All administrators will receive alerts
                </p>
              </div>
            </div>

            <div className="relative flex items-start mt-4">
              <div className="flex items-center h-5">
                <input
                  id="manager-alerts"
                  name="manager-alerts"
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                  defaultChecked
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="manager-alerts"
                  className="font-medium text-gray-700"
                >
                  Department Managers
                </label>
                <p className="text-gray-500">
                  Department-specific alerts for managers
                </p>
              </div>
            </div>

            <div className="relative flex items-start mt-4">
              <div className="flex items-center h-5">
                <input
                  id="custom-email-alerts"
                  name="custom-email-alerts"
                  type="checkbox"
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="custom-email-alerts"
                  className="font-medium text-gray-700"
                >
                  Custom Email Recipients
                </label>
                <p className="text-gray-500">
                  Send to specific email addresses
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTagSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Conversation Tags
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Manage tags used to categorize conversations.
        </p>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h3 className="text-sm font-medium text-gray-900">
              Enable Tagging
            </h3>
            <p className="text-sm text-gray-500">
              Allow conversations to be tagged for easy categorization.
            </p>
          </div>
          <button
            type="button"
            className={`${
              taggingEnabled ? "bg-blue-600" : "bg-gray-200"
            } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
            onClick={() => setTaggingEnabled(!taggingEnabled)}
            aria-pressed="false"
            aria-labelledby="toggle-tagging"
          >
            <span className="sr-only">Toggle tagging</span>
            <span
              aria-hidden="true"
              className={`${
                taggingEnabled ? "translate-x-5" : "translate-x-0"
              } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
            />
          </button>
        </div>

        {taggingEnabled && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900">
              Available Tags
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {tag}
                  <button
                    type="button"
                    className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 focus:outline-none focus:bg-blue-500 focus:text-white"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <span className="sr-only">Remove {tag} tag</span>
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </span>
              ))}
            </div>

            <form className="mt-4 flex" onSubmit={handleAddTag}>
              <input
                type="text"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Add a new tag"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
              />
              <button
                type="submit"
                className="ml-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Add
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="relative flex w-full border-l"
      style={{
        background:
          "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
      }}
    >
      <div className="w-full flex flex-col">
        <ChatTrackerNav />

        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <div className="px-0">
                  <h2 className="font-SFPro font-bold text-2xl text-gray-900">
                    Settings
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Configure how the CES Conversation Tracker works.
                  </p>

                  <div className="mt-6 space-y-1">
                    <button
                      className={`relative block w-full text-left px-3 py-2 text-sm font-medium rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                        activeSection === "general"
                          ? "bg-white text-gray-900 border-blue-200 shadow-sm before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-7 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-primary-blue before:to-blue-400"
                          : "text-gray-700 bg-white/70 border-gray-200/80 hover:bg-blue-50/40 hover:border-blue-100"
                      }`}
                      onClick={() => setActiveSection("general")}
                    >
                      General Settings
                    </button>
                    <button
                      className={`relative block w-full text-left px-3 py-2 text-sm font-medium rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                        activeSection === "alerts"
                          ? "bg-white text-gray-900 border-blue-200 shadow-sm before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-7 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-primary-blue before:to-blue-400"
                          : "text-gray-700 bg-white/70 border-gray-200/80 hover:bg-blue-50/40 hover:border-blue-100"
                      }`}
                      onClick={() => setActiveSection("alerts")}
                    >
                      Alerts & Notifications
                    </button>
                    <button
                      className={`relative block w-full text-left px-3 py-2 text-sm font-medium rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
                        activeSection === "tags"
                          ? "bg-white text-gray-900 border-blue-200 shadow-sm before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-7 before:w-1 before:rounded-full before:bg-gradient-to-b before:from-primary-blue before:to-blue-400"
                          : "text-gray-700 bg-white/70 border-gray-200/80 hover:bg-blue-50/40 hover:border-blue-100"
                      }`}
                      onClick={() => setActiveSection("tags")}
                    >
                      Conversation Tags
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="bg-white border border-blue-100 rounded-xl overflow-hidden flex flex-col">
                  <div
                    className="px-6 py-4 border-b border-blue-100 rounded-t-xl"
                    style={{
                      background:
                        "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                    }}
                  >
                    <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
                      {activeSection === "general"
                        ? "General Settings"
                        : activeSection === "alerts"
                        ? "Alerts & Notifications"
                        : "Conversation Tags"}
                    </p>
                  </div>

                  <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                    {activeSection === "general" && renderGeneralSettings()}
                    {activeSection === "alerts" && renderAlertSettings()}
                    {activeSection === "tags" && renderTagSettings()}
                  </div>

                  <div className="px-4 py-3 bg-gray-50/60 backdrop-blur text-right sm:px-6 border-t border-gray-100">
                    {saveMessage && (
                      <div
                        className={`inline-block mr-4 text-sm ${
                          saveMessage.includes("Error")
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {saveMessage}
                      </div>
                    )}
                    <button
                      type="button"
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-blue to-blue-400 hover:brightness-105 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={handleSaveSettings}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </>
                      ) : (
                        "Save Settings"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatTrackerSettings;
